import {Modal} from '@/components/atoms'
import {CustomDataTable} from '@/components/organisms'
import './UploadHistoryTableModal.scss'
import {MODAL_TYPE} from '@/utils/constants/globalStyle'
import {useQuery} from '@tanstack/react-query'
import {useMemo} from 'react'

/**
 * A reusable UploadHistoryTableModal that accepts:
 * - mainData: an array of row objects for the main table
 * - mainColumns: an array of column definitions for the main table
 * - subData: an array of objects for the sub-rows
 * - subColumns: an array of column definitions for the sub-table
 *
 * The table expands to show subData corresponding to each main row's "id".
 */
const UploadHistoryTableModal = ({
  openUploadHistory,
  setOpenUploadHistory,
  mainData = [],
  mainColumns = [],
  subColumns = [],
  fetchSubRows,
  isDark = true,
  totalRecords = 0,
  setPageIndex = () => {},
}) => {
  // remove all but the first 3 items from mainData
  const mainDataSubset = mainData.slice(0, 3)

  // Component to render sub rows table
  const SubRowsTable = ({parentId}) => {
    const {data = [], isFetching} = useQuery({
      queryKey: ['inventory-upload-errors', parentId, fetchSubRows],
      queryFn: () => (fetchSubRows ? fetchSubRows(parentId) : []),
      enabled: !!fetchSubRows && !!parentId,
    })

    const rows = Array.isArray(data) ? data : data?.data || [];
    const hasLocationId = rows.some(row => !!row.warehouseId);

    // Dynamically determine columns for this file's errors
    const dynamicSubColumns = [
      { columnKey: 'productId', header: 'Product ID' },
      ...(hasLocationId ? [{ columnKey: 'warehouseId', header: 'Warehouse ID' }] : []),
      { columnKey: 'errorMsg', header: 'STATUS (REMARK)' },
    ]

    const transformedSubColumns = useMemo(
      () =>
        dynamicSubColumns.map(col => {
          const isStatusRemark = col.header === 'STATUS (REMARK)'
          const name_of_db_column = isStatusRemark ? 'errorCol-errorMsg' : (col.name_of_db_column ?? col.columnKey)

          return {
            name_of_db_column,
            label_of_column: col.label_of_column ?? col.header,
            filterVariant: col.filterVariant || 'text',
            renderCell: (value, rowData, columnDef) => {
              if (isStatusRemark) {
                // Safely combine errorCol and errorMsg values from rowData
                const errorCol = rowData?.errorCol ?? ''
                const errorMsg = rowData?.errorMsg ?? ''
                return `${errorCol}  ${errorMsg}`
              }

              // Use col.cellData if it exists
              if (col.cellData) {
                try {
                  return col.cellData({row: {original: rowData}}, columnDef)
                } catch {
                  return col.cellData(value, rowData, columnDef)
                }
              }

              return value
            },
          }
        }),
      [dynamicSubColumns]
    )

    const subComponentProps = {
      id: `rw-upload-history-table-sub-component-${parentId}`,
      records: rows,
      columns: transformedSubColumns,
      isDark,
      pageSize: rows.length || 10,
      totalRecords: rows.length,
      enableSelection: false,
      showResetAll: false,
      showColumnFilters: false,
      enableSorting: false,
      isExpandable: false,
      showActions: false,
      isLoading: isFetching,
      showFooter: false,
    }

    return <CustomDataTable {...subComponentProps} />
  }

  /**
   * Enable expand only if at least one row has errors (errorCount > 0)
   */
  const tableHasExpandableRows = Array.isArray(mainDataSubset) && mainDataSubset.some(r => Number(r?.errorCount) > 0)

  /**
   * Renders the sub-rows for an expanded row.
   * Note: we use `row.original.id` to match `parentId` in subData.
   */
  const renderSubComponent = row => {
    // Only rows with errorCount > 0 should be expandable
    if (!(Number(row?.original?.errorCount) > 0)) return null

    const parentId = row.original.fileUploadUUID || row.original.id
    if (fetchSubRows) {
      return (
        <div className="sub-component-container">
          <SubRowsTable parentId={parentId} />
        </div>
      )
    }
    return null
  }

  /**
   * Transform mainColumns to the prop structure expected by CustomDataTable
   * CustomDataTable expects `name_of_db_column` & `label_of_column` keys.
   */
  const transformedColumns = useMemo(
    () =>
      mainColumns.map(col => ({
        name_of_db_column: col.name_of_db_column ?? col.columnKey,
        label_of_column: col.label_of_column ?? col.header, //
        filterVariant: col.filterVariant || 'text',
        ...(col.cellData && {
          renderCell: (value, rowData, columnDef) => {
            try {
              return col.cellData({row: {original: rowData}}, columnDef)
            } catch (e) {
              return col.cellData(value, rowData, columnDef)
            }
          },
        }),
        ...(col.isClickable && {
          isClickable: true,
          onCellClick: col.onCellClick,
        }),
      })),
    [mainColumns]
  )

  /**
   * Default props for the main DataTable
   */
  const defaultProps = {
    id: 'rw-upload-history-table',
    records: mainDataSubset,
    columns: transformedColumns,
    isDark,
    pageSize: 10,
    totalRecords: totalRecords,
    setPageIndex,
    enableSelection: false,
    showResetAll: false,
    showColumnFilters: false,
    enableSorting: false,
    isExpandable: tableHasExpandableRows,
    renderSubComponent,
    showFilterButton: false,
    showFilterToolbar: false,
    rowSelection: {},
    onGlobalSearchChange: undefined,
    selection: null,
    showActions: false,
    showFooter: false,
  }

  return (
    <Modal
      open={openUploadHistory}
      onCloseModal={() => setOpenUploadHistory(false)}
      title="Upload History"
      showModal={openUploadHistory}
      isDark={isDark}
      type={MODAL_TYPE.LARGE}
      content={
        <div className="rw-upload-history-modal-content">
          <CustomDataTable {...defaultProps} />
        </div>
      }
    />
  )
}

export default UploadHistoryTableModal
